import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentSessionSkill } from "@/lib/agent-api"
import { cn } from "@/lib/utils"

export type SkillSlashListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean
}

type Props = {
  items: AgentSessionSkill[]
  query?: string
  command: (skill: AgentSessionSkill) => void
}

export const SkillSlashList = forwardRef<SkillSlashListHandle, Props>(function SkillSlashList(
  { items, query = "", command },
  ref,
) {
  const { t } = useTranslation("workspace")
  const [selected, setSelected] = useState(0)
  /** Accent highlight only after arrow keys / hover — default first item is for Enter, not "in use". */
  const [showSelection, setShowSelection] = useState(false)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const shouldScrollRef = useRef(false)
  const keyboardNavRef = useRef(false)

  useEffect(() => {
    setSelected(0)
    setShowSelection(false)
  }, [items, query])

  useEffect(() => {
    itemRefs.current.length = items.length
  }, [items.length])

  useEffect(() => {
    if (!shouldScrollRef.current) return
    shouldScrollRef.current = false
    itemRefs.current[selected]?.scrollIntoView({ block: "nearest" })
  }, [selected])

  const pick = (index: number) => {
    const item = items[index]
    if (!item) return
    command(item)
  }

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event) => {
        if (items.length === 0) return false
        if (event.key === "ArrowDown") {
          keyboardNavRef.current = true
          shouldScrollRef.current = true
          setShowSelection(true)
          setSelected((s) => (s + 1) % items.length)
          return true
        }
        if (event.key === "ArrowUp") {
          keyboardNavRef.current = true
          shouldScrollRef.current = true
          setShowSelection(true)
          setSelected((s) => (s + items.length - 1) % items.length)
          return true
        }
        if (event.key === "Enter" || event.key === "Tab") {
          if (items.length === 0) return true
          pick(selected)
          return true
        }
        return false
      },
    }),
    // pick closes over items/command; selected needed for Enter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, items, command],
  )

  if (items.length === 0) {
    return (
      <div className="w-72 rounded-lg border border-ui-border bg-ui-bg-elevated p-1 shadow-lg">
        <div className="px-2 py-2 text-[11px] text-ui-text-muted">{t("agent.skillsNothingFound")}</div>
      </div>
    )
  }

  return (
    <div
      className="it2tv-scrollbar max-h-64 w-72 overflow-y-auto rounded-lg border border-ui-border bg-ui-bg-elevated p-1 shadow-lg"
      onMouseMove={() => {
        keyboardNavRef.current = false
      }}
    >
      {items.map((skill, index) => {
        const active = showSelection && index === selected
        return (
          <button
            key={skill.id}
            ref={(node) => {
              itemRefs.current[index] = node
            }}
            type="button"
            aria-selected={active}
            className={cn(
              "flex w-full min-w-0 flex-col items-start gap-0.5 overflow-hidden rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-xs shadow-none transition-colors",
              active ? "bg-ui-accent/10 text-ui-text-primary" : "text-ui-text-primary hover:bg-ui-surface-raised",
            )}
            onMouseEnter={() => {
              if (keyboardNavRef.current) return
              shouldScrollRef.current = false
              setShowSelection(true)
              setSelected(index)
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              pick(index)
            }}
          >
            <span className="block w-full min-w-0 truncate font-medium">{skill.name}</span>
            {skill.description ? (
              <span className="line-clamp-2 w-full min-w-0 text-[10px] break-words text-ui-text-muted">
                {skill.description}
              </span>
            ) : null}
            <span className="block w-full min-w-0 truncate font-mono text-[11px] text-ui-link">/{skill.slug}</span>
          </button>
        )
      })}
    </div>
  )
})
